import Modal from "@/components/atoms/Modal";
import {
  INVALID_FORM_MESSAGE,
  INVALID_MINIMUM_CARACTERE_MESSAGE,
} from "@/constants/validatorMessages";
import { IModalMethods } from "@/interfaces/Modal";
import {
  ActivitySectorForm,
  useGetActivitySectorQuery,
  useStoreActivitySectorMutation,
  useUpdateActivitySectorMutation,
} from "@/services/activitySectors";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import {
  ElementRef,
  ForwardRefRenderFunction,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Col, Form, Row } from "react-bootstrap";
import {
  Controller,
  SubmitErrorHandler,
  SubmitHandler,
  useForm,
} from "react-hook-form";
import { ToastContentProps, toast } from "react-toastify";

import { SelectPaginateAsync } from "@/components/atoms/Select";
import useLoadOptions from "@/hooks/useLoadOptions";

interface StoreActivitySectorModalMethods extends IModalMethods {
  showOrHiddenModal: (id?: string) => void;
}

const StoreActivitySectorModal: ForwardRefRenderFunction<
  StoreActivitySectorModalMethods
> = (_, ref) => {
  const ModalRef = useRef<ElementRef<typeof Modal>>(null);
  const [editingId, setEditingId] = useState<string | undefined>(undefined);

  const {
    handleSubmit,
    register,
    reset,
    setValue,
    control,
    formState: { errors },
  } = useForm<ActivitySectorForm>();

  const { loadOpcosOptions } = useLoadOptions();

  const [storeActivitySectorAsync] = useStoreActivitySectorMutation();
  const [updateActivitySectorAsync] = useUpdateActivitySectorMutation();
  const { data: activitySector } = useGetActivitySectorQuery(editingId || "", {
    skip: !editingId,
  });

  useEffect(() => {
    if (activitySector && editingId) {
      setValue("name", activitySector.name);
      setValue("opco", activitySector.opco);
    }
  }, [activitySector, editingId, setValue]);

  const showOrHiddenModal = (id?: string) => {
    if (id) {
      setEditingId(id);
    } else {
      setEditingId(undefined);
      reset();
    }
    ModalRef.current?.showOrHiddenModal();
  };

  useImperativeHandle(ref, () => ({
    showOrHiddenModal,
  }));

  const onSubmit: SubmitHandler<ActivitySectorForm> = ({ opco, ...data }) => {
    if (!opco) return;

    const mutation = editingId
      ? updateActivitySectorAsync([editingId, { ...data, opcoId: opco.id }])
      : storeActivitySectorAsync({ ...data, opcoId: opco.id });

    toast.promise(mutation.unwrap(), {
      pending: editingId
        ? "Modification du secteur d'activité en cours..."
        : "Ajout du secteur d'activité en cours...",
      success: {
        render() {
          reset();
          showOrHiddenModal();
          return (
            <p style={{ marginBottom: 0, textAlign: "center" }}>
              {editingId
                ? "Le secteur d'activité a bien été modifié 🤩"
                : "Le secteur d'activité a bien été ajouté 🤩"}
            </p>
          );
        },
      },
      error: {
        render({ data }: ToastContentProps<FetchBaseQueryError>) {
          if (data?.status === 422) {
            return "Les champs que vous avez remplis semblent être incorrects.";
          }

          return "Une erreur est survenue";
        },
      },
    });
  };

  const onError: SubmitErrorHandler<ActivitySectorForm> = () =>
    toast.error(INVALID_FORM_MESSAGE);

  return (
    <Modal
      ref={ModalRef}
      title={
        editingId
          ? "Modifier un secteur d'activité"
          : "Ajouter un secteur d'activité"
      }
      requiredLabel
      onSubmit={handleSubmit(onSubmit, onError)}
    >
      <Row className="gx-3">
        <Col lg={6}>
          <Form.Group className="mb-3">
            <Form.Label>
              Nom <span className="text-danger">*</span> :
            </Form.Label>
            <Form.Control
              placeholder="ex : Informatique"
              type="text"
              {...register("name", {
                required: "Le nom est requis",
                minLength: {
                  value: 2,
                  message: INVALID_MINIMUM_CARACTERE_MESSAGE,
                },
              })}
              isInvalid={!!errors.name}
            />
            <Form.Control.Feedback type="invalid">
              {errors.name?.message}
            </Form.Control.Feedback>
            <Form.Text className="text-muted">
              Nom du secteur d'activité (ex: Informatique, Bâtiment, Santé)
            </Form.Text>
          </Form.Group>
        </Col>
        <Col lg={6}>
          <Form.Group className="mb-3">
            <Form.Label>
              OPCO <span className="text-danger">*</span> :
            </Form.Label>
            <Controller
              control={control}
              name="opco"
              rules={{ required: "L'OPCO est requis" }}
              render={({ field }) => (
                <SelectPaginateAsync
                  loadOptions={loadOpcosOptions}
                  getOptionLabel={(option) => option.name}
                  getOptionValue={(option) => option.id}
                  value={field.value}
                  onChange={field.onChange}
                  aria-invalid={!!errors.opco}
                />
              )}
            />
            <Form.Control.Feedback type="invalid">
              {errors.opco?.message}
            </Form.Control.Feedback>
            <Form.Text className="text-muted">
              L'OPCO associé à ce secteur d'activité
            </Form.Text>
          </Form.Group>
        </Col>
      </Row>
    </Modal>
  );
};

export default forwardRef(StoreActivitySectorModal);
